/**
 * The scopes that are administrative configuration, mirrored from
 * `shared/auth/role_templates.py` (`ADMIN_ONLY_SCOPES` /
 * `ADMIN_ONLY_WRITE_SCOPES`).
 *
 * A scope is administrative when changing it alters how the system behaves for
 * the whole tenant — identity, policy, catalogue, integrations — as opposed to
 * recording a business event. Only a tenant admin may hold one, and the
 * services refuse to store one on anyone else however the permission matrix is
 * edited. This copy exists so the portal declines to *offer* what the API
 * would refuse; it is not the enforcement.
 */

/** Withheld from a non-admin entirely — even reading discloses configuration. */
export const ADMIN_ONLY_SCOPES: ReadonlySet<string> = new Set([
  'users',
  'settings',
  'tenant_settings',
  'credentials',
  'api_keys',
  'domains',
  'branding',
  'memberships',
  'roles',
  'merchants',
  'audit_logs',
  'lock_engine',
]);

/**
 * Catalogue scopes any operator reads — a branch name on a contract, a plan
 * name on an application — but only an admin may change.
 */
export const ADMIN_ONLY_WRITE_SCOPES: ReadonlySet<string> = new Set([
  'branches',
  'financing_plans',
  'device_models',
  'document_types',
]);

const WRITE_ACTIONS: ReadonlySet<string> = new Set([
  'create',
  'update',
  'delete',
]);

/** Whether performing `action` on `scope` is administrative. */
export function isAdministrative(scope: string, action: string): boolean {
  if (ADMIN_ONLY_SCOPES.has(scope)) return true;
  return ADMIN_ONLY_WRITE_SCOPES.has(scope) && WRITE_ACTIONS.has(action);
}
